/**
 * Signal-style local decrypted message store.
 * 
 * Messages are E2EE on the server - once decrypted client-side,
 * the plaintext is cached in IndexedDB so historical messages
 * remain viewable even if encryption keys rotate or are lost.
 * 
 * This mirrors how Signal/WhatsApp work: encrypted in transit,
 * plaintext on-device.
 */

const DB_NAME = 'elefa-msg-store';
const DB_VERSION = 2;
const STORE_NAME = 'decrypted_messages';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('room_id', 'room_id', { unique: false });
      }
      // v2: pending_messages store for offline queue
      if (!db.objectStoreNames.contains('pending_messages')) {
        const pending = db.createObjectStore('pending_messages', { keyPath: 'local_id' });
        pending.createIndex('room_id', 'room_id', { unique: false });
        pending.createIndex('status', 'status', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Search decrypted messages in IndexedDB by content substring.
 * Used for local-first search.
 */
export async function searchLocalMessages(query: string): Promise<LocalMessage[]> {
  if (!query || query.length < 2) return [];
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    const all = await new Promise<LocalMessage[]>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    const lower = query.toLowerCase();
    return all.filter(m => m.content?.toLowerCase().includes(lower));
  } catch {
    return [];
  }
}

export interface LocalMessage {
  id: string;
  room_id: string;
  content: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  file_key?: string;
  duration_seconds?: number;
}

/**
 * Cache a batch of successfully decrypted messages.
 * Called after decryption succeeds - stores plaintext locally.
 */
export async function cacheDecryptedMessages(messages: LocalMessage[]): Promise<void> {
  if (messages.length === 0) return;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const msg of messages) {
      store.put(msg);
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    console.warn('[LocalMsgStore] Cache write failed:', e);
  }
}

/**
 * Retrieve a single cached decrypted message by ID.
 * Used as fallback when server-side decryption fails.
 */
export async function getCachedMessage(messageId: string): Promise<LocalMessage | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(messageId);
    const result = await new Promise<LocalMessage | null>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  } catch {
    return null;
  }
}

/**
 * Retrieve all cached messages for a room.
 * Used for instant room loading before server fetch.
 */
export async function getCachedRoomMessages(roomId: string): Promise<LocalMessage[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('room_id');
    const req = index.getAll(roomId);
    const result = await new Promise<LocalMessage[]>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  } catch {
    return [];
  }
}

/**
 * Batch lookup: returns a map of messageId -> LocalMessage for quick fallback.
 */
/**
 * Wipe all local message and ratchet databases on logout.
 * Signal-grade secure deletion - no plaintext remains on device.
 */
export async function clearAllLocalMessages(): Promise<void> {
  try {
    // Delete decrypted message store
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
    // Delete ratchet state store
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase('elefa-ratchet');
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  } catch {
    console.warn('[LocalMsgStore] Failed to clear databases');
  }
}

export async function getCachedMessagesMap(messageIds: string[]): Promise<Map<string, LocalMessage>> {
  const map = new Map<string, LocalMessage>();
  if (messageIds.length === 0) return map;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all(
      messageIds.map(id => new Promise<void>((resolve) => {
        const req = store.get(id);
        req.onsuccess = () => {
          if (req.result) map.set(id, req.result);
          resolve();
        };
        req.onerror = () => resolve();
      }))
    );
    db.close();
  } catch { /* non-critical */ }
  return map;
}
